import React, { useMemo, useState, useEffect } from 'react';
import type { WidgetSize } from '../../types/dashboard';
import type { TimeRange } from '../../types/dashboard';
import type { DailyData } from '../../types';
import { fmtMoneyWithDecimals } from '../../utils/formatters';
import { getDashboardWidgetTimeframe } from '../../lib/dashboardTimeRange';

import {
  extractTradeCost,
  extractTradeSwap,
  getTradePnl,
  getTradeTimestampMs,
  normalizeTradeStatus,
} from '../../lib/trades/authoritativeTradeData';

interface TotalFeesWidgetProps {
  dailyData?: DailyData[];
  trades?: any[];
  size?: WidgetSize;
  timeRange?: TimeRange;
}

export const TotalFeesWidget: React.FC<TotalFeesWidgetProps> = ({ dailyData = [], trades = [], timeRange }) => {
  const displayToToken: Record<string, '24H' | '7D' | '30D' | '12M' | 'YTD'> = {
    'Last 24 Hours': '24H',
    'Last 7 Days': '7D',
    'Last 30 Days': '30D',
    'Last 12 Months': '12M',
    YTD: 'YTD',
  };
  const tokenToDisplay: Record<'24H' | '7D' | '30D' | '12M' | 'YTD', string> = {
    '24H': 'Last 24 Hours',
    '7D': 'Last 7 Days',
    '30D': 'Last 30 Days',
    '12M': 'Last 12 Months',
    YTD: 'YTD',
  };

  const [timeframeDisplay, setTimeframeDisplay] = useState<string>('Last 7 Days');
  const timeframe: '24H' | '7D' | '30D' | '12M' | 'YTD' = displayToToken[timeframeDisplay] ?? '7D';

  useEffect(() => {
    if (timeRange) setTimeframeDisplay(tokenToDisplay[getDashboardWidgetTimeframe(timeRange)]);
  }, [timeRange]);

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as any;
        if (!detail || detail.id !== 'totalFees') return;
        const val = detail.value as '24H' | '7D' | '30D' | '12M' | 'YTD' | string;
        if (val === '24H' || val === '7D' || val === '30D' || val === '12M' || val === 'YTD') {
          setTimeframeDisplay(tokenToDisplay[val]);
        }
      } catch {}
    };
    window.addEventListener('widget-timeframe:selected', handler as EventListener);
    return () => window.removeEventListener('widget-timeframe:selected', handler as EventListener);
  }, []);

  const filteredTrades = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return [];
    const now = Date.now();
    const tfDays = timeframe === '24H' ? 1 : timeframe === '7D' ? 7 : timeframe === '30D' ? 30 : 365;
    const tfMs = tfDays * 24 * 60 * 60 * 1000;
    const startOfYearMs = new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0).getTime();

    return trades.filter((t) => {
      const ts = getTradeTimestampMs(t);
      if (ts == null || !Number.isFinite(ts)) return false;
      if (timeframe === 'YTD') return ts >= startOfYearMs;
      return ts >= now - tfMs;
    });
  }, [trades, timeframe]);

  const totals = useMemo(() => {
    if (filteredTrades.length === 0) {
      const filteredDaily = (dailyData || []).filter((d) => {
        const raw = (d as any).date ?? (d as any).t ?? (d as any).timestamp;
        const ts = typeof raw === 'number' ? raw : typeof raw === 'string' ? Date.parse(raw) : NaN;
        if (!Number.isFinite(ts)) return false;
        const tfDays = timeframe === '24H' ? 1 : timeframe === '7D' ? 7 : timeframe === '30D' ? 30 : 365;
        const tfMs = tfDays * 24 * 60 * 60 * 1000;
        const startOfYearMs = new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0).getTime();
        if (timeframe === 'YTD') return ts >= startOfYearMs;
        return ts >= Date.now() - tfMs;
      });
      const totalPnl = filteredDaily.reduce((acc, d) => acc + (d.pnl || 0), 0);
      const totalTrades = filteredDaily.reduce((acc, d) => acc + ((d as any).trades || 0), 0);
      return { totalFees: 0, totalSwaps: 0, totalTrades, totalPnl };
    }

    let totalFees = 0;
    let totalSwaps = 0;
    let totalPnl = 0;
    for (const t of filteredTrades) {
      totalFees += extractTradeCost(t);
      totalSwaps += extractTradeSwap(t);
      // Open fills can already incur fees, but they do not contribute realized P&L.
      if (normalizeTradeStatus(t) === 'closed') totalPnl += getTradePnl(t);
    }
    return { totalFees, totalSwaps, totalTrades: filteredTrades.length, totalPnl };
  }, [dailyData, filteredTrades, timeframe]);

  // P&L % calculation and pill styling
  // Use realized P&L from this widget's own filtered timeframe. A dashboard-wide
  // total can have a different period when the widget's timeframe is changed.
  const pnlValue = totals.totalPnl || 0;

  let pctStr = 'No P&L';
  let isGain = true;
  let pillClasses = 'bg-emerald-600/10 text-emerald-600 dark:text-emerald-200 border border-emerald-600/40';

  if (pnlValue > 0) {
    const pctRaw = (totals.totalFees / pnlValue) * 100;
    pctStr = pctRaw < 1 ? `${pctRaw.toFixed(1)}% of P&L` : `${Math.round(pctRaw)}% of P&L`;
  } else if (pnlValue <= 0 && totals.totalFees > 0) {
    pctStr = 'Fee Drag';
    isGain = false;
  } else {
    pctStr = 'No P&L';
  }

  if (!isGain || pctStr === 'No P&L') {
    pillClasses = 'bg-red-500/10 text-red-500 dark:text-red-500 border border-red-500/40';
  }
  if (pctStr === 'No P&L') {
    pillClasses = 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 border border-zinc-500/40';
  }

  return (
    <div className="h-full w-full pt-0 pb-3 px-4 relative">
      <div className="flex items-start justify-between w-full">
        {/* Left: amount + avg cost (constrain so it fits) */}
        <div className="flex flex-col items-start min-w-0">
          <span
            data-testid="total-fees-amount"
            className="min-w-0 truncate -mt-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-700 dark:text-zinc-100"
          >
            {fmtMoneyWithDecimals(totals.totalFees, 2)}
          </span>
          <div
            data-testid="total-fees-average-cost"
            className="mt-1 min-w-0 truncate pt-[6px] text-sm text-zinc-600 dark:text-zinc-400"
          >
            Avg cost per trade: <span className="font-medium">{fmtMoneyWithDecimals(totals.totalTrades > 0 ? totals.totalFees / totals.totalTrades : 0, 2)}</span>
          </div>
        </div>

        {/* Right: pill and swaps line, stacked */}
        <div className="flex flex-col items-end flex-shrink-0 ml-4 gap-2">
          <div className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold cursor-default ${pillClasses}`}>
            {pctStr}
          </div>
          <div
            data-testid="total-fees-swaps"
            className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400"
          >
            <span className="w-2 h-2 rounded-full" style={{ background: '#f59e0b' }} />
            <span>Swaps: <span className="font-medium text-zinc-700 dark:text-zinc-100">{fmtMoneyWithDecimals(totals.totalSwaps, 2)}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
};
